import type { ProjectCheckoutDeletionPreview } from "@cmmd-forge/contracts";

import type { ProjectGroupRemovalTarget } from "~/projectGroupRemovalRunner";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { Input } from "../ui/input";

export interface ProjectCheckoutDeletionControlProps {
  readonly checked: boolean;
  readonly disabled: boolean;
  readonly previews: ReadonlyMap<string, ProjectCheckoutDeletionPreview>;
  readonly onCheckedChange: (checked: boolean) => void;
  readonly onRetry: () => void;
  readonly confirmation: string;
  readonly onConfirmationChange: (confirmation: string) => void;
}

export function projectGroupRemovalTargetKey(target: ProjectGroupRemovalTarget): string {
  return `${target.environmentId}:${target.projectId}`;
}

function deletionMethodDisposition(method: ProjectCheckoutDeletionPreview["method"]) {
  switch (method) {
    case "trash":
      return "Will move to Trash.";
    case "git-worktree":
      return "Will remove this Git worktree.";
    case "permanent":
      return "Will be permanently deleted.";
    case "unavailable":
      return "File deletion is unavailable.";
  }
}

function previewBlockerMessage(preview: ProjectCheckoutDeletionPreview): string | null {
  const blockers = preview.blockers.filter((blocker) => blocker !== "dirty_git_state");
  if (blockers.length === 0) return null;
  if (blockers.includes("process_inspection_failed")) {
    return "Forge couldn't verify running processes. Files were not touched.";
  }
  if (blockers.includes("active_processes")) {
    return "A running process is using this checkout. Stop it before deleting files.";
  }
  if (blockers.includes("git_inspection_failed")) {
    return "Forge couldn't check for uncommitted work. Files were not touched.";
  }
  return "File deletion is unavailable. Files were not touched.";
}

function fileDisposition(preview: ProjectCheckoutDeletionPreview | undefined): string | null {
  if (!preview) return null;
  const blocker = previewBlockerMessage(preview);
  if (blocker) return blocker;
  if (preview.method === "unavailable" || preview.fingerprint === null) {
    return "File deletion is unavailable. Files were not touched.";
  }
  const disposition = deletionMethodDisposition(preview.method);
  return preview.dirty ? `Contains uncommitted work. ${disposition}` : disposition;
}

export function ProjectGroupRemovalTargetList(props: {
  readonly targets: readonly ProjectGroupRemovalTarget[];
  readonly previews: ReadonlyMap<string, ProjectCheckoutDeletionPreview>;
  readonly expanded?: boolean;
}) {
  const threadCount = props.targets.reduce((total, target) => total + target.threadCount, 0);
  const scopeLabel = `${props.targets.length} ${props.targets.length === 1 ? "checkout" : "checkouts"} · ${threadCount} ${threadCount === 1 ? "thread" : "threads"}`;

  return (
    <details
      open={props.expanded}
      className="rounded-[var(--control-radius)] border border-border/60 bg-secondary/30"
    >
      <summary className="min-h-touch cursor-pointer px-3 py-3 text-sm font-medium text-foreground md:min-h-8 md:py-2">
        {scopeLabel}
      </summary>
      <ul className="grid gap-3 border-t border-border/60 px-3 py-2.5 text-xs">
        {props.targets.map((target) => {
          const preview = props.previews.get(projectGroupRemovalTargetKey(target));
          const disposition = fileDisposition(preview);
          const blocked =
            preview?.blockers.some((blocker) => blocker !== "dirty_git_state") ?? false;
          return (
            <li key={projectGroupRemovalTargetKey(target)} className="grid min-w-0 gap-1">
              <span className="truncate font-medium text-foreground">{target.label}</span>
              <code className="break-all text-muted-foreground">{target.path}</code>
              {props.targets.length > 1 ? (
                <span className="text-muted-foreground">
                  {target.threadCount} {target.threadCount === 1 ? "thread" : "threads"}
                </span>
              ) : null}
              {disposition ? (
                <span
                  role={blocked ? "alert" : undefined}
                  className={blocked ? "text-destructive-foreground" : "text-muted-foreground"}
                >
                  {disposition}
                </span>
              ) : null}
            </li>
          );
        })}
      </ul>
    </details>
  );
}

export function ProjectCheckoutDeletionControl(props: ProjectCheckoutDeletionControlProps) {
  const needsDirtyConfirmation =
    props.checked && [...props.previews.values()].some((preview) => preview.dirty);
  const hasBlockingPreview =
    props.checked &&
    [...props.previews.values()].some((preview) =>
      preview.blockers.some((blocker) => blocker !== "dirty_git_state"),
    );
  let status = "Files stay on disk.";
  if (props.checked) {
    if (props.previews.size === 0) status = "Safety check required.";
    else if (hasBlockingPreview) status = "Safety check failed.";
    else if (needsDirtyConfirmation) status = "Uncommitted work found.";
    else status = "Safety check passed.";
  }
  return (
    <div className="grid gap-2 border-t border-border/60 pt-3">
      <label className="flex min-h-touch items-center gap-2 text-sm text-foreground md:min-h-8">
        <Checkbox
          aria-label="Delete checkout files"
          checked={props.checked}
          disabled={props.disabled}
          onCheckedChange={(checked) => props.onCheckedChange(checked === true)}
        />
        <span className="grid gap-0.5">
          <span className="font-medium">Delete checkout files</span>
          <span className="text-xs font-normal text-muted-foreground">{status}</span>
        </span>
      </label>
      {hasBlockingPreview ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="min-h-touch justify-self-start md:min-h-8"
          disabled={props.disabled}
          onClick={props.onRetry}
        >
          Retry check
        </Button>
      ) : null}
      {needsDirtyConfirmation ? (
        <Input
          aria-label="Confirm uncommitted work deletion"
          value={props.confirmation}
          onChange={(event) => props.onConfirmationChange(event.currentTarget.value)}
          placeholder="Type delete uncommitted work if prompted"
        />
      ) : null}
    </div>
  );
}
